import MarkerClusterer, { MarkerClustererOptions } from '@googlemaps/markerclustererplus';

import { toGoogleMapLatLng } from '../../helpers';
import { MapBaseOverlay } from '../base-overlay';
import { BaseMarker } from '../markers/base-marker';
import { IClusterLayerOptions } from './cluster-layer-options';

const DEFAULT_OPTIONS: IClusterLayerOptions = {
  maxZoom: 18,
  gridSize: 60,
  zoomOnClick: true,
  minimumClusterSize: 2,
};

export class GoogleMapClusterLayer extends MapBaseOverlay {
  private readonly _markers = new Map<string, BaseMarker>();
  private readonly _googleMarkers = new Map<string, google.maps.Marker>();

  private _googleMarkerClusterer: MarkerClusterer;
  private readonly _googleMarkerClustererOptions: MarkerClustererOptions;

  constructor(options?: IClusterLayerOptions) {
    super();

    this._googleMarkerClustererOptions = {
      imagePath: '/assets/clusters/m',
      ...DEFAULT_OPTIONS,
      ...options,
    };
  }

  public addMarker(marker: BaseMarker): void {
    const markerId = marker.getId();

    if (this._markers.has(markerId)) {
      return;
    }

    this._markers.set(markerId, marker);

    const googleMarker = this._toGoogleMapMarker(marker);
    this._googleMarkers.set(markerId, googleMarker);

    if (this._googleMarkerClusterer) {
      this._googleMarkerClusterer.addMarker(googleMarker);
    }
  }

  public removeMarker(marker: BaseMarker): void {
    const markerId = marker.getId();

    if (!this._markers.has(markerId)) {
      return;
    }

    this._markers.delete(markerId);

    const googleMarker = this._googleMarkers.get(markerId);
    this._googleMarkers.delete(markerId);

    if (this._googleMarkerClusterer) {
      this._googleMarkerClusterer.removeMarker(googleMarker);
    }
  }

  protected onAdd(): void {
    this._googleMarkerClusterer = new MarkerClusterer(
      this.map.mapInstance,
      Array.from(this._googleMarkers.values()),
      this._googleMarkerClustererOptions,
    );
  }

  protected onRemove(): void {
    if (this._googleMarkerClusterer) {
      this._googleMarkerClusterer.setMap(null);
      delete this._googleMarkerClusterer;
    }
  }

  protected draw(): void {}

  private _toGoogleMapMarker(marker: BaseMarker): google.maps.Marker {
    let googleMap: google.maps.Map | google.maps.StreetViewPanorama;

    const googleMapMarker = {
      getPosition: () => {
        return toGoogleMapLatLng(marker.getLonLat());
      },
      setMap: (map) => {
        googleMap = map;

        if (map) {
          marker.addTo(this.map);
        } else {
          marker.remove();
        }
      },
      getMap: () => googleMap,
      getVisible: () => true,
      getDraggable: () => false,
    };

    return googleMapMarker as google.maps.Marker;
  }
}
